// src/components/PreOnboardingModal.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../css/Forms.css';
import roles from '../data/role'; // your role.js export

/** =========================================
 *  Small helpers
 *  ======================================= */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const saveJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const loadJSON = (k, f = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; } };
const isHHMM = (s) => typeof s === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
const toMinutes = (hhmm) => { const [h, m] = (hhmm || '0:0').split(':'); return (+h) * 60 + (+m); };

const toList = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',') : []);
const uniq = (arr) => [...new Set(arr.map(s => String(s).trim()).filter(Boolean))];
const toArr = (v) => uniq(toList(v));
const eqArr = (a = [], b = []) => a.length === b.length && a.every((v, i) => v === b[i]);

/** =========================================
 *  Fallback option libraries (used only if role.js lacks data)
 *  ======================================= */
const FALLBACK = {
  workEthic:  ['Proactive', 'Detail-Oriented', 'Team Player', 'Self-Starter', 'Communicates Well', 'Fast Learner'],
  hardSkills: ['Photoshop', 'Illustrator', 'Blender', 'Maya', 'Unity', 'Unreal', 'C#', 'JIRA', 'Git'],
  genre:      ['Fantasy', 'Anime', 'Casual', 'Midcore', 'Sci-Fi', 'Horror'],
  shift:      ['Day', 'Night', 'Flexible'],
  schedule:   ['Yes', 'No', 'Limited'],

  primaryTools: ['Photoshop', 'Illustrator', 'Blender', 'Maya', 'Substance Painter', 'Unity', 'Unreal', 'ZBrush'],
  fileFormats:  ['PSD', 'PNG', 'JPG', 'TIFF', 'SVG', 'FBX', 'OBJ'],
  plugins:      ['After Effects', 'Procreate', 'Amplify', 'Cinemachine'],
  gameTypes:    ['Mobile', 'PC', 'Console', 'Web'],
  gameEngines:  ['Unity', 'Unreal', 'Godot', 'Custom'],
  collabTools:  ['Git', 'Perforce', 'SVN', 'Jira', 'Trello', 'Asana', 'Notion'],
  commsTools:   ['Slack', 'Teams', 'Zoom', 'Discord'],
};

const urgencyOpts = [
  'Critical/Urgent',
  'High Priority',
  'Medium Priority',
  'Low Priority',
];

const assessBehavioralOpts = [
  { label: 'Big Five (OCEAN)', value: 'ocean' },
  { label: 'DISC', value: 'disc' },
  { label: 'SJT', value: 'sjt' },
  { label: 'Hogan', value: 'hogan' },
  { label: 'SHL Talent', value: 'shl' },
  { label: 'Caliper Profile', value: 'caliper' },
  { label: 'PI Behavioral', value: 'pi' },
];

const assessSkillsOpts = [
  { label: 'Cognitive Ability', value: 'cognitive' },
  { label: 'Logical', value: 'logical' },
  { label: 'Numerical', value: 'numerical' },
  { label: 'Verbal', value: 'verbal' },
  { label: 'Typing', value: 'typing' },
  { label: 'Role-specific Battery', value: 'role' },
];

/** =========================================
 *  role.js helpers
 *  ======================================= */
const normLevelNum = (s = '') => {
  const v = String(s).toLowerCase().trim();
  if (v.startsWith('tier 1') || v.startsWith('entry')) return 1;
  if (v.startsWith('tier 2') || v.startsWith('mid')) return 2;
  if (v.startsWith('tier 3') || v.startsWith('senior')) return 3;
  return 0;
};
const levelSort = (a, b) => {
  const na = normLevelNum(a) || 999, nb = normLevelNum(b) || 999;
  if (na !== nb) return na - nb;
  return String(a).localeCompare(String(b));
};

// unique role names
const roleNamesFromData = (() => {
  const set = new Set((Array.isArray(roles) ? roles : []).map(r => r.role).filter(Boolean));
  return Array.from(set).sort((a,b)=>a.localeCompare(b));
})();

// map role -> unique talent levels in order
const levelsByRole = (() => {
  const map = {};
  for (const r of (Array.isArray(roles) ? roles : [])) {
    const roleName = r.role;
    const lvl = String(r.talentLevel || '').trim();
    if (!roleName || !lvl) continue;
    if (!map[roleName]) map[roleName] = [];
    if (!map[roleName].includes(lvl)) map[roleName].push(lvl);
  }
  Object.keys(map).forEach(k => map[k].sort(levelSort));
  return map;
})();

// find the specific record for role + level (with cross-label matching)
function findRoleRecord(roleTitle, lvl) {
  if (!roleTitle) return null;
  // try exact match first
  let rec = (roles || []).find(r => r.role === roleTitle && String(r.talentLevel).trim() === String(lvl).trim());
  if (rec) return rec;
  // try normalized numeric match (Entry/Tier 1 etc.)
  const want = normLevelNum(lvl);
  if (want) {
    rec = (roles || []).find(r => r.role === roleTitle && normLevelNum(r.talentLevel) === want);
    if (rec) return rec;
  }
  // fallback: first record for role
  return (roles || []).find(r => r.role === roleTitle) || null;
}

const multiline = (v) => Array.isArray(v) ? v.join('\n') : (v || '');

/** =========================================
 *  CheckList
 *  ======================================= */
function CheckList({ label, options, selected, onChange, allowAdd = true, addPlaceholder = 'Add item...' }) {
  const [custom, setCustom] = useState('');
  const toggle = (v) => {
    const has = selected.includes(v);
    onChange(has ? selected.filter(x => x !== v) : [...selected, v]);
  };
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!selected.includes(v)) onChange([...selected, v]);
    setCustom('');
  };
  const uniqueSelected = Array.from(new Set(selected));

  return (
    <div className="po-field">
      <label className="po-label">{label}</label>
      <div className="po-checklist">
        {options.map((opt) => (
          <label key={opt} className="po-check">
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {allowAdd && (
        <div className="po-addrow">
          <input
            className="po-input"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={addPlaceholder}
          />
          <button type="button" className="po-btn po-btn-ghost" onClick={addCustom}>Add</button>
        </div>
      )}
      <div className="po-selected-wrap">
        <div className="po-selected-header">
          <span className="po-selected-title">Selected</span>
          <span className="po-selected-count">{uniqueSelected.length}</span>
        </div>
        {uniqueSelected.length ? (
          <div className="po-badges">
            {uniqueSelected.map((s) => (
              <span key={s} className="po-badge" title="Click to remove" onClick={() => toggle(s)}>
                <span className="po-badge-text">{s}</span>
                <span className="po-badge-close" aria-hidden>×</span>
              </span>
            ))}
          </div>
        ) : (
          <div className="po-selected-empty">No selections yet — pick some above!</div>
        )}
      </div>
      <div className="po-section-divider" />
    </div>
  );
}

/** =========================================
 *  AssessmentPicker
 *  ======================================= */
function AssessmentPicker({
  label, options, selected, onChangeSelected, purchasesCount, onChangePurchasesCount, plan = 'Free'
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const maxSelections = plan.toLowerCase() === 'free' ? 1 + (purchasesCount || 0) : options.length;

  const isDisabled = (value) => {
    if (selected.includes(value)) return false;
    if (selected.length >= maxSelections) return true;
    return false;
  };

  const toggle = (value) => {
    const has = selected.includes(value);
    if (has) onChangeSelected(selected.filter((v) => v !== value));
    else if (selected.length < maxSelections) onChangeSelected([...selected, value]);
  };

  const confirmBuy = (ok) => {
    if (ok) {
      onChangePurchasesCount((purchasesCount || 0) + 1);
      alert('You have successfully purchased an additional assessment slot for $10 ');
    }
    setShowConfirm(false);
  };

  return (
    <div className="po-field">
      <label className="po-label">{label}</label>
      {!showConfirm ? (
        <>
          <div className="po-tip">
            {plan.toLowerCase() === 'free'
              ? `You can select up to ${maxSelections} assessment${maxSelections > 1 ? 's' : ''}.`
              : 'All assessments available; multiple selections allowed.'}
          </div>
          <div className="po-checklist">
            {options.map((opt) => {
              const disabled = isDisabled(opt.value);
              return (
                <label key={opt.value} className={`po-check ${disabled ? 'po-locked' : ''}`}>
                  <input type="checkbox" disabled={disabled} checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
                  <span>{opt.label}{disabled && !selected.includes(opt.value) ? ' 🔒' : ''}</span>
                </label>
              );
            })}
          </div>
          {plan.toLowerCase() === 'free' && (
            <div className="po-actions">
              <button type="button" className="po-btn po-btn-primary" onClick={() => setShowConfirm(true)}>
                Buy Assessments ($10 each)
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="po-confirm-panel" role="dialog" aria-modal="true">
          <div className="po-confirm-text">
            Are you sure you want to buy an additional assessment slot for <b>$10</b>?
          </div>
          <div className="po-confirm-actions">
            <button type="button" className="po-btn po-btn-primary" onClick={() => confirmBuy(true)}>Yes, Buy</button>
            <button type="button" className="po-btn po-btn-ghost" onClick={() => confirmBuy(false)}>No, Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** =========================================
 *  Main modal (Wizard) — now connected to role.js for Sections 1, 3, and 4
 *  ======================================= */
export default function PreOnboardingModal({
  isOpen,
  onClose,
  onSubmit,
  plan = 'Free',
  defaultRole = roleNamesFromData[0] || '' // fallback to first available role
}) {
  const FREE_SLOTS_LIMIT = 10;
  const draftKey = 'PREONBOARDING_DRAFT_V2';
  const draft = loadJSON(draftKey, null);

  // Build initial form from role.js
  const firstRole = useMemo(() => {
    const r = (draft?.roleTitle && roleNamesFromData.includes(draft.roleTitle))
      ? draft.roleTitle
      : (roleNamesFromData.includes(defaultRole) ? defaultRole : (roleNamesFromData[0] || ''));
    return r;
  }, [defaultRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstLevel = useMemo(() => {
    const levels = levelsByRole[firstRole] || [];
    const draftLevel = draft?.roleTier;
    if (draft && draftLevel && levels.some(l => normLevelNum(l) === normLevelNum(draftLevel) || l === draftLevel)) {
      return draftLevel;
    }
    return levels[0] || '';
  }, [firstRole]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Build a fresh, role.js-driven form */
  const makeInitialForm = (roleTitle = firstRole, roleTier = firstLevel) => {
    const rec = findRoleRecord(roleTitle, roleTier);
    return {
      // Section 1 (Role)
      roleTitle,
      roleTier,
      employmentType: 'Full-Time',
      seniorityLevel: 'Mid-Level',
      workArrangement: 'Remote',
      reportingLine: 'Project Manager',
      reportingLineOther: '',
      roleObjective: multiline(rec?.roleObjective),
      coreCapabilities: multiline(rec?.coreCapabilities),
      supportedWorkflows: multiline(rec?.supportedWorkflows),
      keyDeliverables: multiline(rec?.keyDeliverables),

      // Section 2 (Hiring)
      talentsNeeded: 0,
      candidateSlots: 0,
      preferredStartDate: '',
      budgetRange: '',
      budgetRangeUSD: '',
      budgetRangePHP: '',
      maxBudgetCap: '',
      contingencyPreference: 'Auto-replace from pool',
      interviewFormat: 'Video Call',
      interviewFormatOther: '',
      availabilityForInterview: '',
      engagementDuration: 'Mid-Term [6–12 months]',
      timeline: 'Immediate', // kept for backward-compat (unused here)
      interviewStart: '',
      interviewEnd: '',
      interviewTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      urgency: 'Select Urgency',

      // Section 3 (Talent Profile)
      workEthicTraits: [],
      hardSkills: [],
      genres: [],
      mustHaveSkills: '',
      niceToHaveSkills: '',
      languagesRequired: [],
      languagesOther: '',
      shift: toArr(rec?.shiftAvailability)[0] || 'Day',
      scheduleFlexibility: toArr(rec?.workScheduleFlexibility)[0] || 'Yes',
      portfolioRequirement: 'Yes',
      certificationRequirement: 'None',
      certificationOther: '',

      // Section 4 (Tech & Creative)
      primaryTools: [],
      fileFormats: [],
      plugins: [],
      gameTypes: [],
      gameEngines: [],
      collabTools: [],
      commsTools: [],
      commsToolsOther: '',
      hardwareRequirements: '',
      clientToolsAccess: 'Not sure',
      securityCompliance: [],
      securityComplianceOther: '',

      // Section 5 (Assessments)
      assessBehavioral: [],
      assessSkills: [],
      assessBehavioralPurchasesCount: 0,
      assessSkillsPurchasesCount: 0,
      addOnSelections: [],
      addOnOther: '',
      fastTrackUpgrade: 'No',
      customAssessmentFile: null,

      // Section 6 (Notes)
      notes: '',
      collaborationModel: 'Individual Contributor',
      successCriteria: '',
    };
  };

  const [form, setForm] = useState(() => draft ?? makeInitialForm());
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0); // 0..5
  const suppressPersist = useRef(false);

  const STEPS = useMemo(() => ([
    { key: 'role',      title: 'Role Information' },
    { key: 'hiring',    title: 'Hiring Parameters' },
    { key: 'talent',    title: 'Talent Profile Requirements' },
    { key: 'tech',      title: 'Technical & Creative' },
    { key: 'assess',    title: 'Assessments & Add-ons' },
    { key: 'notes',     title: 'Additional Notes' },
  ]), []);
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // Persist draft unless explicitly suppressed
  useEffect(() => {
    if (suppressPersist.current) { suppressPersist.current = false; return; }
    saveJSON(draftKey, form);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const saveDraft = () => saveJSON(draftKey, form);

  // USD to PHP conversion (approximate rate: 1 USD = 56 PHP)
  const USD_TO_PHP_RATE = 56;
  const convertUSDToPHP = (usdText) => {
    if (!usdText || !usdText.trim()) return '';
    
    // Extract numbers from USD text (e.g., "$1,500–$2,500" -> "1500-2500")
    const numbers = usdText.match(/\d+/g);
    if (!numbers || numbers.length === 0) return '';
    
    try {
      const converted = numbers.map(num => {
        const usdAmount = parseInt(num.replace(/,/g, ''));
        const phpAmount = Math.round(usdAmount * USD_TO_PHP_RATE);
        return phpAmount.toLocaleString();
      });
      
      if (converted.length === 1) {
        return `₱${converted[0]}`;
      } else if (converted.length === 2) {
        return `₱${converted[0]}–₱${converted[1]}`;
      } else {
        return `₱${converted.join('–')}`;
      }
    } catch (e) {
      return '';
    }
  };

  // Auto-convert USD to PHP when budget range changes
  useEffect(() => {
    if (form.budgetRangeUSD) {
      const phpAmount = convertUSDToPHP(form.budgetRangeUSD);
      setForm((f) => ({ ...f, budgetRangePHP: phpAmount }));
    } else {
      setForm((f) => ({ ...f, budgetRangePHP: '' }));
    }
  }, [form.budgetRangeUSD]);

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    setErrors({});
    setStep(0);
    suppressPersist.current = true;
    setForm(makeInitialForm());
  };

  // When Role Title changes, ensure tier is valid for that role
  useEffect(() => {
    const available = levelsByRole[form.roleTitle] || [];
    const has = available.some(l => normLevelNum(l) === normLevelNum(form.roleTier) || l === form.roleTier);
    if (!has) setForm((f) => ({ ...f, roleTier: available[0] || '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.roleTitle]);

  // Auto-fill role fields whenever roleTitle or roleTier changes
  useEffect(() => {
    const rec = findRoleRecord(form.roleTitle, form.roleTier);
    if (!rec) return;
    setForm((f) => ({
      ...f,
      roleObjective: multiline(rec.roleObjective),
      coreCapabilities: multiline(rec.coreCapabilities),
      supportedWorkflows: multiline(rec.supportedWorkflows),
      keyDeliverables: multiline(rec.keyDeliverables),
    }));
  }, [form.roleTitle, form.roleTier]);

  /** =========================================
   *  Section 3 & 4: OPTION SOURCES FROM role.js
   *  ======================================= */
  const selectedRec = useMemo(
    () => findRoleRecord(form.roleTitle, form.roleTier),
    [form.roleTitle, form.roleTier]
  );

  const sec3Options = useMemo(() => {
    const workEthic = toArr(selectedRec?.workEthic);
    const hardSkills = toArr(selectedRec?.hardSkills);
    const genre = toArr(selectedRec?.genre);
    const shift = toArr(selectedRec?.shiftAvailability);
    const schedule = toArr(selectedRec?.workScheduleFlexibility);
    return {
      workEthic: workEthic.length ? workEthic : FALLBACK.workEthic,
      hardSkills: hardSkills.length ? hardSkills : FALLBACK.hardSkills,
      genre: genre.length ? genre : FALLBACK.genre,
      shift: shift.length ? shift : FALLBACK.shift,
      schedule: schedule.length ? schedule : FALLBACK.schedule,
    };
  }, [selectedRec]);

  const sec4Options = useMemo(() => {
    const primaryTools = toArr(selectedRec?.primaryTools);
    const fileFormats  = toArr(selectedRec?.fileFormats);
    const plugins      = toArr(selectedRec?.Plugins || selectedRec?.plugins);
    const gameTypes    = toArr(selectedRec?.gameType);
    const gameEngines  = toArr(selectedRec?.gameEngine || selectedRec?.gameEngines);
    const collabTools  = toArr(selectedRec?.versionControl);
    const commsTools   = toArr(selectedRec?.communication);
    return {
      primaryTools: primaryTools.length ? primaryTools : FALLBACK.primaryTools,
      fileFormats:  fileFormats.length  ? fileFormats  : FALLBACK.fileFormats,
      plugins:      plugins.length      ? plugins      : FALLBACK.plugins,
      gameTypes:    gameTypes.length    ? gameTypes    : FALLBACK.gameTypes,
      gameEngines:  gameEngines.length  ? gameEngines  : FALLBACK.gameEngines,
      collabTools:  collabTools.length  ? collabTools  : FALLBACK.collabTools,
      commsTools:   commsTools.length   ? commsTools   : FALLBACK.commsTools,
    };
  }, [selectedRec]);

  // Keep selections valid when options change (avoid values not in new role's lists)
  useEffect(() => {
    setForm((f) => {
      const nextWorkEthic = f.workEthicTraits.filter(x => sec3Options.workEthic.includes(x));
      const nextHardSkills = f.hardSkills.filter(x => sec3Options.hardSkills.includes(x));
      const nextGenres = f.genres.filter(x => sec3Options.genre.includes(x));
      const nextShift = sec3Options.shift.includes(f.shift) ? f.shift : (sec3Options.shift[0] || '');
      const nextSchedule = sec3Options.schedule.includes(f.scheduleFlexibility) ? f.scheduleFlexibility : (sec3Options.schedule[0] || '');

      if (
        eqArr(nextWorkEthic, f.workEthicTraits) &&
        eqArr(nextHardSkills, f.hardSkills) &&
        eqArr(nextGenres, f.genres) &&
        nextShift === f.shift &&
        nextSchedule === f.scheduleFlexibility
      ) return f;

      return {
        ...f,
        workEthicTraits: nextWorkEthic,
        hardSkills: nextHardSkills,
        genres: nextGenres,
        shift: nextShift,
        scheduleFlexibility: nextSchedule,
      };
    });
  }, [sec3Options.workEthic, sec3Options.hardSkills, sec3Options.genre, sec3Options.shift, sec3Options.schedule]);

  useEffect(() => {
    setForm((f) => {
      const nextPrimary = f.primaryTools.filter(x => sec4Options.primaryTools.includes(x));
      const nextFormats = f.fileFormats.filter(x => sec4Options.fileFormats.includes(x));
      const nextPlugins = f.plugins.filter(x => sec4Options.plugins.includes(x));
      const nextGameTypes = f.gameTypes.filter(x => sec4Options.gameTypes.includes(x));
      const nextEngines = f.gameEngines.filter(x => sec4Options.gameEngines.includes(x));
      const nextCollab = f.collabTools.filter(x => sec4Options.collabTools.includes(x));
      const nextComms = f.commsTools.filter(x => sec4Options.commsTools.includes(x));

      if (
        eqArr(nextPrimary, f.primaryTools) &&
        eqArr(nextFormats, f.fileFormats) &&
        eqArr(nextPlugins, f.plugins) &&
        eqArr(nextGameTypes, f.gameTypes) &&
        eqArr(nextEngines, f.gameEngines) &&
        eqArr(nextCollab, f.collabTools) &&
        eqArr(nextComms, f.commsTools)
      ) return f;

      return {
        ...f,
        primaryTools: nextPrimary,
        fileFormats: nextFormats,
        plugins: nextPlugins,
        gameTypes: nextGameTypes,
        gameEngines: nextEngines,
        collabTools: nextCollab,
        commsTools: nextComms,
      };
    });
  }, [
    sec4Options.primaryTools,
    sec4Options.fileFormats,
    sec4Options.plugins,
    sec4Options.gameTypes,
    sec4Options.gameEngines,
    sec4Options.collabTools,
    sec4Options.commsTools,
  ]);

  /** =========================================
   *  Validation & flow
   *  ======================================= */
  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.roleTitle)            e.roleTitle = 'Role title is required.';
      if (!form.roleTier)             e.roleTier = 'Talent level is required.';
      if (!form.employmentType)       e.employmentType = 'Employment type is required.';
      if (!form.seniorityLevel)       e.seniorityLevel = 'Seniority level is required.';
      if (!form.workArrangement)      e.workArrangement = 'Work arrangement is required.';
      if (!form.reportingLine)        e.reportingLine = 'Reporting line is required.';
      if (form.reportingLine === 'Other' && !form.reportingLineOther.trim()) e.reportingLineOther = 'Please specify reporting line.';
      if (!form.roleObjective.trim()) e.roleObjective = 'Missing role objective for this selection.';
    }
    if (s === 1) {
      const nTalents = Number(form.talentsNeeded);
      const nSlots   = Number(form.candidateSlots);
      const limit    = (plan.toLowerCase() === 'free') ? FREE_SLOTS_LIMIT : 9999;

      if (!Number.isFinite(nTalents) || nTalents < 1) e.talentsNeeded = 'Enter a valid number.';
      if (!Number.isFinite(nSlots) || nSlots < 1 || nSlots > limit) e.candidateSlots = `Candidate slots must be between 1 and ${limit}.`;
      if (!form.preferredStartDate) e.preferredStartDate = 'Preferred start date is required.';
      if (!form.budgetRangeUSD || !form.budgetRangeUSD.trim()) e.budgetRangeUSD = 'Please provide a budget range in USD.';
      if (!form.maxBudgetCap || !form.maxBudgetCap.trim()) e.maxBudgetCap = 'Please provide maximum budget cap.';
      if (!form.contingencyPreference) e.contingencyPreference = 'Contingency preference is required.';
      if (!form.interviewFormat) e.interviewFormat = 'Interview format is required.';
      if (form.interviewFormat === 'Other' && !form.interviewFormatOther.trim()) e.interviewFormatOther = 'Please specify interview format.';
      if (!form.availabilityForInterview) e.availabilityForInterview = 'Availability for client interview is required.';
      if (!form.engagementDuration) e.engagementDuration = 'Engagement duration is required.';
      if (!form.urgency) e.urgency = 'Select an urgency level.';

      if (!isHHMM(form.interviewStart)) e.interviewStart = 'Pick a valid start time (HH:MM).';
      if (!isHHMM(form.interviewEnd))   e.interviewEnd   = 'Pick a valid end time (HH:MM).';
      if (isHHMM(form.interviewStart) && isHHMM(form.interviewEnd)) {
        const startM = toMinutes(form.interviewStart);
        const endM   = toMinutes(form.interviewEnd);
        if (startM >= endM) e.interviewRange = 'Start time must be earlier than end time.';
      }
      if (!form.interviewTz) e.interviewTz = 'Select a timezone.';
    }
    if (s === 2) {
      if (!form.mustHaveSkills || !form.mustHaveSkills.trim()) e.mustHaveSkills = 'Must-have skills are required.';
      if (!form.languagesRequired.length) e.languagesRequired = 'At least one language is required.';
      if (!form.portfolioRequirement) e.portfolioRequirement = 'Portfolio requirement is required.';
      if (!form.certificationRequirement) e.certificationRequirement = 'Certification requirement is required.';
      if (form.certificationRequirement === 'Other' && !form.certificationOther.trim()) e.certificationOther = 'Please specify certification.';
      if (!form.workEthicTraits.length) e.workEthicTraits = 'Select at least one trait.';
      if (!form.hardSkills.length)      e.hardSkills = 'Select at least one skill.';
      if (!form.genres.length)          e.genres = 'Select at least one preference.';
    }
    if (s === 3) {
      if (!form.clientToolsAccess) e.clientToolsAccess = 'Client tools access is required.';
      if (!form.primaryTools.length) e.primaryTools = 'Select at least one tool.';
      if (!form.fileFormats.length)  e.fileFormats  = 'Select at least one file format.';
      if (!form.plugins.length)      e.plugins      = 'Select at least one plugin.';
      if (!form.gameTypes.length)    e.gameTypes    = 'Select at least one game type.';
      if (!form.gameEngines.length)  e.gameEngines  = 'Select at least one engine.';
      if (!form.collabTools.length)  e.collabTools  = 'Select at least one collaboration tool.';
      if (!form.commsTools.length)   e.commsTools   = 'Select at least one communication tool.';
    }
    if (s === 4) {
      if (form.assessBehavioral.length === 0) e.assessBehavioral = 'Choose at least one behavioral assessment.';
      if (form.assessSkills.length === 0) e.assessSkills = 'Choose at least one skills assessment.';
      if (!form.fastTrackUpgrade) e.fastTrackUpgrade = 'Fast-track upgrade selection is required.';
    }
    if (s === 5) {
      if (!form.collaborationModel) e.collaborationModel = 'Collaboration model is required.';
      if (!form.successCriteria || !form.successCriteria.trim()) e.successCriteria = 'Success criteria are required.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (!validateStep(step)) return; setStep((s) => Math.min(totalSteps - 1, s + 1)); };
  const goBack = () => setStep((s) => Math.max(0, 5 - 1));
  const onSubmitForm = (e) => {
    e.preventDefault();
    if (step !== totalSteps - 1) return;
    if (!validateStep(step)) return;
    saveJSON(draftKey, form);
    onSubmit?.(form);
    alert('Pre-Onboarding request submitted!');
    onClose?.();
  };
  const preventEnterSubmit = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); };

  if (!isOpen) return null;

  const currentLevels = levelsByRole[form.roleTitle] || [];

  return (
    <div className="po-overlay" onClick={onClose}>
      <div className="po-modal po-modal--wizard" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="po-header">
          <h2 className="po-title">Pre-Onboarding Request Form</h2>
          <button type="button" className="po-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Progress */}
        <div className="po-progress">
          <div className="po-progress-top"><div className="po-progress-num">{progressPct}%</div></div>
          <div className="po-progress-bar"><div className="po-progress-fill" style={{ width: `${progressPct}%` }} /></div>
        </div>

        <form className="po-body" onSubmit={onSubmitForm} onKeyDown={preventEnterSubmit}>
          {/* ====== STEP 1: ROLE (driven by role.js) ====== */}
          {step === 0 && (
            <section className="po-card">
              <h3 className="po-h3">Section 1: Role Information</h3>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Role Title</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.roleTitle}
                    onChange={(e) => handleChange('roleTitle', e.target.value)}
                    required
                  >
                    {roleNamesFromData.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {errors.roleTitle && <div className="po-error">{errors.roleTitle}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Talent Level</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.roleTier}
                    onChange={(e) => handleChange('roleTier', e.target.value)}
                    required
                  >
                    {currentLevels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                  {errors.roleTier && <div className="po-error">{errors.roleTier}</div>}
                </div>
              </div>

              <div className="po-grid4">
                <div className="po-field">
                  <label className="po-label">Employment Type</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.employmentType}
                    onChange={(e) => handleChange('employmentType', e.target.value)}
                    required
                  >
                    <option value="Full-Time">Full-Time</option>
                    <option value="Project-Based">Project-Based</option>
                    <option value="Contractual">Contractual</option>
                    <option value="Internship">Internship</option>
                  </select>
                  {errors.employmentType && <div className="po-error">{errors.employmentType}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Seniority Level Needed</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.seniorityLevel}
                    onChange={(e) => handleChange('seniorityLevel', e.target.value)}
                    required
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid-Level">Mid-Level</option>
                    <option value="Senior">Senior</option>
                  </select>
                  {errors.seniorityLevel && <div className="po-error">{errors.seniorityLevel}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Work Arrangement</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.workArrangement}
                    onChange={(e) => handleChange('workArrangement', e.target.value)}
                    required
                  >
                    <option value="Onsite">Onsite</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Remote">Remote</option>
                  </select>
                  {errors.workArrangement && <div className="po-error">{errors.workArrangement}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Reporting Line</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.reportingLine}
                    onChange={(e) => handleChange('reportingLine', e.target.value)}
                    required
                  >
                    <option value="Project Manager">Project Manager</option>
                    <option value="Tech Lead">Tech Lead</option>
                    <option value="Art Director">Art Director</option>
                    <option value="Producer">Producer</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reportingLine && <div className="po-error">{errors.reportingLine}</div>}
                </div>
              </div>

              {form.reportingLine === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify reporting line</label>
                  <input
                    className="po-input"
                    placeholder="Enter reporting line details..."
                    value={form.reportingLineOther}
                    onChange={(e) => handleChange('reportingLineOther', e.target.value)}
                    required
                  />
                  {errors.reportingLineOther && <div className="po-error">{errors.reportingLineOther}</div>}
                </div>
              )}

              <div className="po-grid1">
                <div className="po-field">
                  <label className="po-label">Role Objective</label>
                  <textarea className="po-textarea" rows={3} value={form.roleObjective} readOnly />
                  {errors.roleObjective && <div className="po-error">{errors.roleObjective}</div>}
                </div>

                <div className="po-grid2">
                  <div className="po-field">
                    <label className="po-label">Core Capabilities</label>
                    <textarea className="po-textarea-core" rows={4} value={form.coreCapabilities} readOnly />
                  </div>
                  <div className="po-field">
                    <label className="po-label">Supported Workflows</label>
                    <textarea className="po-textarea-core" rows={4} value={form.supportedWorkflows} readOnly />
                  </div>
                </div>

                <div className="po-field">
                  <label className="po-label">Key Deliverables</label>
                  <textarea
                    className="po-textarea-key"
                    rows={3}
                    placeholder="Edit or add any deliverables for this engagement…"
                    value={form.keyDeliverables}
                    onChange={(e) => handleChange('keyDeliverables', e.target.value)}
                    readOnly
                  />
                </div>

                <div className="po-field po-right">
                  <button
                    type="button"
                    className="po-btn po-btn-ghost"
                    onClick={() => {
                      const rec = findRoleRecord(form.roleTitle, form.roleTier);
                      setForm((f) => ({
                        ...f,
                        roleObjective: multiline(rec?.roleObjective),
                        coreCapabilities: multiline(rec?.coreCapabilities),
                        supportedWorkflows: multiline(rec?.supportedWorkflows),
                        keyDeliverables: multiline(rec?.keyDeliverables),
                      }));
                    }}
                  >
                    Reload from role.js
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 2: HIRING ====== */}
          {step === 1 && (
            <section className="po-card">
              <h3 className="po-h3">Section 2: Hiring Parameters</h3>

              <div className="po-grid4">
                <div className="po-field">
                  <label className="po-label">How Many Talents Needed</label>
                  <input
                    type="number"
                    className="po-input"
                    min={1}
                    value={form.talentsNeeded}
                    onChange={(e) => handleChange('talentsNeeded', clamp(Number(e.target.value || 0), 1, 9999))}
                    required
                  />
                  {errors.talentsNeeded && <div className="po-error">{errors.talentsNeeded}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">
                    Candidate Slots {(plan.toLowerCase() === 'free') ? `(max ${FREE_SLOTS_LIMIT} on Free)` : ''}
                  </label>
                  <input
                    type="number"
                    className="po-input"
                    min={1}
                    max={(plan.toLowerCase() === 'free') ? FREE_SLOTS_LIMIT : 9999}
                    value={form.candidateSlots}
                    onChange={(e) =>
                      handleChange('candidateSlots', clamp(Number(e.target.value || 0), 1, (plan.toLowerCase() === 'free') ? FREE_SLOTS_LIMIT : 9999))
                    }
                    required
                  />
                  {errors.candidateSlots && <div className="po-error">{errors.candidateSlots}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Preferred Start Date</label>
                  <input
                    type="date"
                    className="po-input"
                    value={form.preferredStartDate}
                    onChange={(e) => handleChange('preferredStartDate', e.target.value)}
                    required
                  />
                  {errors.preferredStartDate && <div className="po-error">{errors.preferredStartDate}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Urgency</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.urgency}
                    onChange={(e) => handleChange('urgency', e.target.value)}
                    required
                  >
                    {urgencyOpts.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {errors.urgency && <div className="po-error">{errors.urgency}</div>}
                </div>
              </div>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Budget Range (per hire) - USD</label>
                  <input
                    className="po-input"
                    placeholder="e.g., $1,500–$2,500"
                    value={form.budgetRangeUSD}
                    onChange={(e) => handleChange('budgetRangeUSD', e.target.value)}
                    required
                  />
                  {errors.budgetRangeUSD && <div className="po-error">{errors.budgetRangeUSD}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Budget Range (per hire) - PHP (Auto-converted)</label>
                  <input
                    className="po-input"
                    placeholder="Auto-converted from USD"
                    value={form.budgetRangePHP}
                    readOnly
                  />
                </div>
              </div>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Maximum Budget Cap (per role/per hire)</label>
                  <input
                    className="po-input"
                    placeholder="e.g., $3,000"
                    value={form.maxBudgetCap}
                    onChange={(e) => handleChange('maxBudgetCap', e.target.value)}
                    required
                  />
                  {errors.maxBudgetCap && <div className="po-error">{errors.maxBudgetCap}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Contingency Preference</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.contingencyPreference}
                    onChange={(e) => handleChange('contingencyPreference', e.target.value)}
                    required
                  >
                    <option value="Auto-replace from pool">Auto-replace from pool</option>
                    <option value="Wait for client approval">Wait for client approval</option>
                    <option value="Not applicable">Not applicable</option>
                  </select>
                  {errors.contingencyPreference && <div className="po-error">{errors.contingencyPreference}</div>}
                </div>
              </div>

              <div className="po-grid2">
                <div className="po-field">
                  <label className="po-label">Interview Format</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.interviewFormat}
                    onChange={(e) => handleChange('interviewFormat', e.target.value)}
                    required
                  >
                    <option value="Video Call">Video Call</option>
                    <option value="Technical Test">Technical Test</option>
                    <option value="Live Coding/Design Session">Live Coding/Design Session</option>
                    <option value="Panel Interview">Panel Interview</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.interviewFormat && <div className="po-error">{errors.interviewFormat}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Engagement Duration</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.engagementDuration}
                    onChange={(e) => handleChange('engagementDuration', e.target.value)}
                    required
                  >
                    <option value="Short-Term Project [&lt;6 months]">Short-Term Project [&lt;6 months]</option>
                    <option value="Mid-Term [6–12 months]">Mid-Term [6–12 months]</option>
                    <option value="Long-Term [12+ months]">Long-Term [12+ months]</option>
                    <option value="Indefinite">Indefinite</option>
                  </select>
                  {errors.engagementDuration && <div className="po-error">{errors.engagementDuration}</div>}
                </div>
              </div>

              {form.interviewFormat === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify interview format</label>
                  <input
                    className="po-input"
                    placeholder="Enter interview format details..."
                    value={form.interviewFormatOther}
                    onChange={(e) => handleChange('interviewFormatOther', e.target.value)}
                    required
                  />
                  {errors.interviewFormatOther && <div className="po-error">{errors.interviewFormatOther}</div>}
                </div>
              )}

              <div className="po-field">
                <label className="po-label">Availability for Client Interview (Date & Time)</label>
                <input
                  type="datetime-local"
                  className="po-input"
                  value={form.availabilityForInterview}
                  onChange={(e) => handleChange('availabilityForInterview', e.target.value)}
                  required
                />
                {errors.availabilityForInterview && <div className="po-error">{errors.availabilityForInterview}</div>}
              </div>

              <div className="po-grid2">
                <div className="po-field po-span2">
                  <label className="po-label">Preferred Interview Time (From → Until)</label>
                  <div className="po-time-range">
                    <input type="time" className="po-input" step="60" value={form.interviewStart} onChange={(e) => handleChange('interviewStart', e.target.value)} required />
                    <span className="po-range-sep">to</span>
                    <input type="time" className="po-input" step="60" value={form.interviewEnd} onChange={(e) => handleChange('interviewEnd', e.target.value)} required />
                  </div>
                  {errors.interviewStart && <div className="po-error">{errors.interviewStart}</div>}
                  {errors.interviewEnd && <div className="po-error">{errors.interviewEnd}</div>}
                  {errors.interviewRange && <div className="po-error">{errors.interviewRange}</div>}
                </div>

                <div className="po-field po-span2">
                  <label className="po-label">Time Zone</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.interviewTz}
                    onChange={(e) => handleChange('interviewTz', e.target.value)}
                    required
                  >
                    {[
                      { value: 'UTC', label: 'UTC' },
                      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
                      { value: 'America/Denver', label: 'Mountain Time (MT)' },
                      { value: 'America/Chicago', label: 'Central Time (CT)' },
                      { value: 'America/New_York', label: 'Eastern Time (ET)' },
                      { value: 'Europe/London', label: 'UK (London)' },
                      { value: 'Europe/Berlin', label: 'Central Europe (Berlin)' },
                      { value: 'Asia/Singapore', label: 'Singapore (SGT)'},
                      { value: 'Asia/Manila', label: 'Philippines (PHT)'},
                      { value: 'Asia/Tokyo', label: 'Japan (JST)' },
                      { value: 'Australia/Sydney', label: 'Australia (Sydney)' },
                    ].map((tz) => (<option key={tz.value} value={tz.value}>{tz.label}</option>))}
                  </select>
                  {errors.interviewTz && <div className="po-error">{errors.interviewTz}</div>}
                  <div className="po-help">Time is interpreted in the selected timezone.</div>
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 3: TALENT PROFILE (checkbox choices from role.js) ====== */}
          {step === 2 && (
            <section className="po-card">
              <div className="po-card-head">
                <h3 className="po-h3">Section 3: Talent Profile Requirements</h3>
                <p className="po-subtitle">Choose core traits & skills, then set availability preferences.</p>
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Top 3 Must-Have Skills (mandatory)</label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="List the top 3 essential skills required for this role..."
                    value={form.mustHaveSkills}
                    onChange={(e) => handleChange('mustHaveSkills', e.target.value)}
                    required
                  />
                  {errors.mustHaveSkills && <div className="po-error">{errors.mustHaveSkills}</div>}
                </div>

                <div className="po-field">
                  <label className="po-label">Nice to-Have Skills (optional)</label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="List additional skills that would be beneficial but not required..."
                    value={form.niceToHaveSkills}
                    onChange={(e) => handleChange('niceToHaveSkills', e.target.value)}
                  />
                </div>
              </div>

              <div className="po-group">
                <CheckList
                  label="Languages Required"
                  options={['English', 'Japanese', 'Korean', 'Mandarin', 'Spanish']}
                  selected={form.languagesRequired}
                  onChange={(v) => handleChange('languagesRequired', v)}
                  allowAdd={true}
                  addPlaceholder="Add other language..."
                />
                {errors.languagesRequired && <div className="po-error">{errors.languagesRequired}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Work Ethic / Traits"
                  options={sec3Options.workEthic}
                  selected={form.workEthicTraits}
                  onChange={(v) => handleChange('workEthicTraits', v)}
                />
                {errors.workEthicTraits && <div className="po-error">{errors.workEthicTraits}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Hard Skills"
                  options={sec3Options.hardSkills}
                  selected={form.hardSkills}
                  onChange={(v) => handleChange('hardSkills', v)}
                />
                {errors.hardSkills && <div className="po-error">{errors.hardSkills}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Genre"
                  options={sec3Options.genre}
                  selected={form.genres}
                  onChange={(v) => handleChange('genres', v)}
                />
                {errors.genres && <div className="po-error">{errors.genres}</div>}
              </div>

              <div className="po-grid3">
                <div className="po-field">
                  <label className="po-label">Shift Availability</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.shift}
                    onChange={(e) => handleChange('shift', e.target.value)}
                  >
                    {sec3Options.shift.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="po-field">
                  <label className="po-label">Work Schedule Flexibility</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.scheduleFlexibility}
                    onChange={(e) => handleChange('scheduleFlexibility', e.target.value)}
                  >
                    {sec3Options.schedule.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="po-field">
                  <label className="po-label">Portfolio/Work Sample Requirement</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.portfolioRequirement}
                    onChange={(e) => handleChange('portfolioRequirement', e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.portfolioRequirement && <div className="po-error">{errors.portfolioRequirement}</div>}
                </div>
              </div>

              <div className="po-field">
                <label className="po-label">Certification Requirement</label>
                <select
                  className="po-input po-select-dark"
                  value={form.certificationRequirement}
                  onChange={(e) => handleChange('certificationRequirement', e.target.value)}
                  required
                >
                  <option value="Unity Certified">Unity Certified</option>
                  <option value="Scrum Master">Scrum Master</option>
                  <option value="PMP">PMP</option>
                  <option value="Other">Other</option>
                  <option value="None">None</option>
                </select>
                {errors.certificationRequirement && <div className="po-error">{errors.certificationRequirement}</div>}
              </div>

              {form.certificationRequirement === 'Other' && (
                <div className="po-field">
                  <label className="po-label">Please specify certification</label>
                  <input
                    className="po-input"
                    placeholder="Enter certification details..."
                    value={form.certificationOther}
                    onChange={(e) => handleChange('certificationOther', e.target.value)}
                    required
                  />
                  {errors.certificationOther && <div className="po-error">{errors.certificationOther}</div>}
                </div>
              )}
            </section>
          )}

          {/* ====== STEP 4: TECH & CREATIVE (checkbox choices from role.js) ====== */}
          {step === 3 && (
            <section className="po-card">
              <h3 className="po-h3">Section 4: Technical & Creative Requirements</h3>

              <div className="po-group">
                <CheckList
                  label="Primary Tools / Software"
                  options={sec4Options.primaryTools}
                  selected={form.primaryTools}
                  onChange={(v) => handleChange('primaryTools', v)}
                />
                {errors.primaryTools && <div className="po-error">{errors.primaryTools}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="File Formats Handled"
                  options={sec4Options.fileFormats}
                  selected={form.fileFormats}
                  onChange={(v) => handleChange('fileFormats', v)}
                />
                {errors.fileFormats && <div className="po-error">{errors.fileFormats}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Plugins"
                  options={sec4Options.plugins}
                  selected={form.plugins}
                  onChange={(v) => handleChange('plugins', v)}
                />
                {errors.plugins && <div className="po-error">{errors.plugins}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Game Type"
                  options={sec4Options.gameTypes}
                  selected={form.gameTypes}
                  onChange={(v) => handleChange('gameTypes', v)}
                />
                {errors.gameTypes && <div className="po-error">{errors.gameTypes}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Game Engines"
                  options={sec4Options.gameEngines}
                  selected={form.gameEngines}
                  onChange={(v) => handleChange('gameEngines', v)}
                />
                {errors.gameEngines && <div className="po-error">{errors.gameEngines}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Version Control & Collaboration"
                  options={sec4Options.collabTools}
                  selected={form.collabTools}
                  onChange={(v) => handleChange('collabTools', v)}
                />
                {errors.collabTools && <div className="po-error">{errors.collabTools}</div>}
              </div>

              <div className="po-group">
                <CheckList
                  label="Communication Tools"
                  options={sec4Options.commsTools}
                  selected={form.commsTools}
                  onChange={(v) => handleChange('commsTools', v)}
                  allowAdd={true}
                  addPlaceholder="Add other communication tool..."
                />
                {errors.commsTools && <div className="po-error">{errors.commsTools}</div>}
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Hardware/Device Requirements</label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="e.g., VR-ready PC, iOS dev kit, specific hardware requirements..."
                    value={form.hardwareRequirements}
                    onChange={(e) => handleChange('hardwareRequirements', e.target.value)}
                  />
                </div>
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Client Tools Access Provided</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.clientToolsAccess}
                    onChange={(e) => handleChange('clientToolsAccess', e.target.value)}
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                  {errors.clientToolsAccess && <div className="po-error">{errors.clientToolsAccess}</div>}
                </div>
              </div>

              <div className="po-group">
                <CheckList
                  label="Security/Compliance Needs"
                  options={['NDA signing', 'IP restrictions', 'Limited access accounts']}
                  selected={form.securityCompliance}
                  onChange={(v) => handleChange('securityCompliance', v)}
                  allowAdd={true}
                  addPlaceholder="Add other security requirement..."
                />
                {errors.securityCompliance && <div className="po-error">{errors.securityCompliance}</div>}
              </div>
            </section>
          )}

          {/* ====== STEP 5: ASSESSMENTS ====== */}
          {step === 4 && (
            <section className="po-card">
              <h3 className="po-h3">Section 5: Assessments & Add-ons</h3>
              <div className="po-grid2">
                <AssessmentPicker
                  label="Behavioral & Personality Assessments"
                  options={assessBehavioralOpts}
                  selected={form.assessBehavioral}
                  onChangeSelected={(v) => handleChange('assessBehavioral', v)}
                  purchasesCount={form.assessBehavioralPurchasesCount}
                  onChangePurchasesCount={(cnt) => handleChange('assessBehavioralPurchasesCount', cnt)}
                  plan={plan}
                />
                <AssessmentPicker
                  label="Skills & Aptitude Testing"
                  options={assessSkillsOpts}
                  selected={form.assessSkills}
                  onChangeSelected={(v) => handleChange('assessSkills', v)}
                  purchasesCount={form.assessSkillsPurchasesCount}
                  onChangePurchasesCount={(cnt) => handleChange('assessSkillsPurchasesCount', cnt)}
                  plan={plan}
                />
              </div>
              {errors.assessBehavioral && <div className="po-error">{errors.assessBehavioral}</div>}
              {errors.assessSkills && <div className="po-error">{errors.assessSkills}</div>}

              <div className="po-group">
                <CheckList
                  label="Add-On Selection Confirmation"
                  options={['Anti-Terror Screening', 'Social Media Screening', 'Credit History Check', 'Psychological Exam', 'HIV/Hep B Screening']}
                  selected={form.addOnSelections}
                  onChange={(v) => handleChange('addOnSelections', v)}
                  allowAdd={true}
                  addPlaceholder="Add other add-on..."
                />
                {errors.addOnSelections && <div className="po-error">{errors.addOnSelections}</div>}
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Fast-Track Upgrade Selection</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.fastTrackUpgrade}
                    onChange={(e) => handleChange('fastTrackUpgrade', e.target.value)}
                    required
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes (Auto-fee displayed)</option>
                  </select>
                  {form.fastTrackUpgrade === 'Yes' && (
                    <div className="po-tip">Fast-track upgrade fee: $50 per candidate</div>
                  )}
                  {errors.fastTrackUpgrade && <div className="po-error">{errors.fastTrackUpgrade}</div>}
                </div>
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Custom Assessment Upload (PDF/DOC)</label>
                  <input
                    type="file"
                    className="po-input"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleChange('customAssessmentFile', file);
                      }
                    }}
                  />
                  {form.customAssessmentFile && (
                    <div className="po-tip">Selected file: {form.customAssessmentFile.name}</div>
                  )}
                  {errors.customAssessmentFile && <div className="po-error">{errors.customAssessmentFile}</div>}
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 6: NOTES ====== */}
          {step === 5 && (
            <section className="po-card">
              <h3 className="po-h3">Section 6: Additional Notes</h3>
              
              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Expected Collaboration Model</label>
                  <select
                    className="po-input po-select-dark"
                    value={form.collaborationModel}
                    onChange={(e) => handleChange('collaborationModel', e.target.value)}
                    required
                  >
                    <option value="Individual Contributor">Individual Contributor</option>
                    <option value="Part of a Team">Part of a Team</option>
                    <option value="Client-Facing">Client-Facing</option>
                  </select>
                  {errors.collaborationModel && <div className="po-error">{errors.collaborationModel}</div>}
                </div>
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Success Criteria</label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="We consider this role successful if..."
                    value={form.successCriteria}
                    onChange={(e) => handleChange('successCriteria', e.target.value)}
                    required
                  />
                  {errors.successCriteria && <div className="po-error">{errors.successCriteria}</div>}
                </div>
              </div>

              <div className="po-group">
                <div className="po-field">
                  <label className="po-label">Special Instructions</label>
                  <textarea
                    className="po-textarea"
                    rows={4}
                    placeholder='e.g., "Need VR experience, anime-style art"'
                    value={form.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* Wizard footer */}
          <div className="po-footer po-footer--wizard">
            <div className="po-footer-left">
              <button type="button" className="po-btn po-btn-ghost" onClick={saveDraft}>Save Draft</button>
              <button type="button" className="po-btn po-btn-ghost" onClick={clearDraft}>Clear Draft</button>
            </div>
            <div className="po-footer-right">
              <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
              {step > 0 && <button type="button" className="po-btn" onClick={goBack}>Back</button>}
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  className="po-btn po-btn-primary"
                  onClick={(e) => { e.preventDefault(); goNext(); }}
                  formNoValidate
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="po-btn po-btn-primary">Submit</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
